import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Gem, Gift, Swords, Trophy, Check, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { SignInRequired } from "@/components/SignInRequired";

const TITLE = "Quests, Chests & Leagues - Inquira";
const DESC = "Turn studying into a game: daily and weekly quests, reward chests, gems and a live weekly XP league.";

export const Route = createFileRoute("/game")({
  component: GamePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

interface Quest {
  id: string;
  title: string;
  metric: string;
  target: number;
  progress: number;
  reward_xp: number;
  reward_gems: number;
  period: string;
  claimed_at: string | null;
  expires_at: string;
}
interface Chest {
  id: string;
  tier: string;
  unlock_at: string;
}
interface Standing {
  user_id: string;
  display_name: string;
  xp: number;
  rank: number;
  is_me: boolean;
}
interface GameState {
  gems: number;
  quests_completed: number;
  chests_opened: number;
  quests: Quest[];
  chests: Chest[];
  league: { season_ends: string | null; my_xp: number; my_rank: number | null; standings: Standing[] };
}

function GamePage() {
  const { user, isLoading } = useAuth();
  const [state, setState] = useState<GameState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data, error } = await (supabase.rpc as never as (n: string) => Promise<{ data: unknown; error: { message: string } | null }>)("get_game_state");
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    const res = data as GameState & { error?: string };
    if (res?.error) return;
    setState(res);
  }, []);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  const claim = async (id: string) => {
    setBusy(id);
    const { data } = await (supabase.rpc as never as (n: string, a: object) => Promise<{ data: { ok?: boolean; xp?: number; gems?: number; error?: string } }>)("claim_quest", { _quest_id: id });
    setBusy(null);
    if (data?.ok) toast.success(`+${data.xp} XP and +${data.gems} gems — a chest is waiting!`);
    else toast.error(data?.error === "incomplete" ? "Finish the quest first." : "Could not claim that reward.");
    load();
  };

  const open = async (id: string) => {
    setBusy(id);
    const { data } = await (supabase.rpc as never as (n: string, a: object) => Promise<{ data: { ok?: boolean; reward?: { gems: number; xp: number }; error?: string } }>)("open_chest", { _chest_id: id });
    setBusy(null);
    if (data?.ok && data.reward) toast.success(`Chest opened: +${data.reward.gems} gems, +${data.reward.xp} XP`);
    else toast.error("Could not open that chest.");
    load();
  };

  if (isLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="size-6 animate-spin text-ink-muted" /></div>;
  }
  if (!user) return <SignInRequired title="Sign in to play" description="Quests, chests and leagues track your real study activity." />;

  const daily = state?.quests.filter((q) => q.period === "daily") ?? [];
  const weekly = state?.quests.filter((q) => q.period !== "daily") ?? [];

  return (
    <div className="min-h-screen max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> Home
        </Link>
        <div className="flex items-center gap-2 text-sm font-bold text-ink">
          <Gem className="size-4 text-neon-cyan" /> {state?.gems ?? 0}
        </div>
      </div>

      <header>
        <h1 className="text-3xl font-display font-black text-ink">Quests &amp; Leagues</h1>
        <p className="text-sm text-ink-muted mt-1">
          Everything you already do — solving, quizzes, flashcards — feeds your quests, chests and weekly league automatically.
        </p>
      </header>

      <QuestList title="Daily quests" quests={daily} busy={busy} onClaim={claim} />
      <QuestList title="Weekly quest" quests={weekly} busy={busy} onClaim={claim} />

      <section className="rounded-2xl border border-rule bg-card p-4">
        <h2 className="font-bold text-ink flex items-center gap-2 mb-3"><Gift className="size-4 text-neon-yellow" /> Chests</h2>
        {state?.chests.length ? (
          <div className="flex flex-wrap gap-3">
            {state.chests.map((c) => (
              <button
                key={c.id}
                onClick={() => open(c.id)}
                disabled={busy === c.id}
                className="rounded-xl border border-rule px-4 py-3 text-sm font-semibold text-ink hover:bg-rule/30 transition-colors capitalize disabled:opacity-50"
              >
                {busy === c.id ? <Loader2 className="size-4 animate-spin" /> : `Open ${c.tier} chest`}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-muted">No chests waiting. Claim a completed quest to earn one.</p>
        )}
      </section>

      <section className="rounded-2xl border border-rule bg-card p-4">
        <h2 className="font-bold text-ink flex items-center gap-2 mb-1"><Trophy className="size-4 text-neon-yellow" /> Weekly league</h2>
        <p className="text-xs text-ink-muted mb-3">
          {state?.league.my_rank ? `You are #${state.league.my_rank} with ${state.league.my_xp} XP this week.` : "Earn XP to enter this week's league."}
          {state?.league.season_ends ? ` Season ends ${new Date(state.league.season_ends).toLocaleDateString()}.` : ""}
        </p>
        <ol className="space-y-1">
          {state?.league.standings.map((s) => (
            <li
              key={s.user_id}
              className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${s.is_me ? "bg-rule/40 font-bold text-ink" : "text-ink-muted"}`}
            >
              <span>#{s.rank} {s.is_me ? "You" : s.display_name}</span>
              <span>{s.xp} XP</span>
            </li>
          ))}
          {!state?.league.standings.length && <li className="text-sm text-ink-muted">No entries yet this week.</li>}
        </ol>
      </section>

      <p className="text-xs text-ink-muted flex items-center gap-2">
        <Swords className="size-3.5" /> Want to compete with people you know? Try <Link to="/friends" className="underline">friend challenges</Link>.
      </p>
    </div>
  );
}

function QuestList({ title, quests, busy, onClaim }: { title: string; quests: Quest[]; busy: string | null; onClaim: (id: string) => void }) {
  if (!quests.length) return null;
  return (
    <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
      <h2 className="font-bold text-ink">{title}</h2>
      {quests.map((q) => {
        const pct = Math.min(100, Math.round((Number(q.progress) / Number(q.target)) * 100));
        const done = Number(q.progress) >= Number(q.target);
        return (
          <div key={q.id} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-ink">{q.title}</span>
              {q.claimed_at ? (
                <span className="text-xs text-ink-muted flex items-center gap-1"><Check className="size-3.5" /> Claimed</span>
              ) : (
                <button
                  onClick={() => onClaim(q.id)}
                  disabled={!done || busy === q.id}
                  className="text-xs font-bold rounded-full px-3 py-1 border border-rule text-ink disabled:opacity-40 hover:bg-rule/30 transition-colors"
                >
                  {busy === q.id ? "…" : `Claim +${q.reward_xp} XP`}
                </button>
              )}
            </div>
            <Progress value={pct} className="h-2" />
            <p className="text-xs text-ink-muted">{Math.round(Number(q.progress))} / {Math.round(Number(q.target))}</p>
          </div>
        );
      })}
    </section>
  );
}